import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

private const val LAST_LEAGUE_KEY = "schedule_last_league_id"

/** Round to jump to "today": the round currently in progress, else the next upcoming one, else (season over) the last. */
private fun currentRoundNumber(roundSlots: List<SeasonRoundSlotDto>): Int? {
    if (roundSlots.isEmpty()) return null
    val sorted = roundSlots.sortedBy { it.roundNumber }
    fun fromEpoch(s: SeasonRoundSlotDto) = isoEpochDay(s.dateFrom)
    fun toEpoch(s: SeasonRoundSlotDto) = s.dateTo?.let { isoEpochDay(it) } ?: fromEpoch(s)
    val today = isoEpochDay(todayIso())
    sorted.firstOrNull { fromEpoch(it) <= today && today <= toEpoch(it) }?.let { return it.roundNumber }
    val first = sorted.first()
    if (today < fromEpoch(first)) return first.roundNumber
    val last = sorted.last()
    if (today > toEpoch(last)) return last.roundNumber
    return sorted.first { fromEpoch(it) > today }.roundNumber
}

@Composable
fun PublicScheduleScreen(
    season: SeasonDto?,
    leagues: List<SeasonLeagueDto>,
    matches: List<MatchWithLeagueDto>,
    roundSlots: List<SeasonRoundSlotDto>,
) {
    var selectedLeagueId by remember { mutableStateOf(localStorage.getItem(LAST_LEAGUE_KEY) ?: "all") }
    var teamNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selectedMatch by remember { mutableStateOf<MatchWithLeagueDto?>(null) }

    fun selectLeague(id: String) {
        selectedLeagueId = id
        localStorage.setItem(LAST_LEAGUE_KEY, id)
    }

    LaunchedEffect(season?.id) {
        if (season == null) return@LaunchedEffect
        loading = true
        try {
            teamNames = Api.fetchAllTeamsForSeason(null, season.id).associate { it.teamId to (it.teamNameOverride ?: it.team?.name ?: it.teamId) }
            error = null
        } catch (e: Throwable) {
            error = "Načtení rozlosování selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    // A league remembered from a previous season may no longer exist here -- fall back to "all".
    LaunchedEffect(leagues) {
        if (leagues.isNotEmpty() && selectedLeagueId != "all" && leagues.none { it.id == selectedLeagueId }) {
            selectLeague("all")
        }
    }

    val currentRound = remember(roundSlots) { currentRoundNumber(roundSlots) }

    Div(attrs = { classes("card") }) {
        if (season == null) {
            Text("Právě není žádná aktivní sezóna.")
            return@Div
        }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        Div(attrs = { classes("actions") }) {
            Button(attrs = {
                if (selectedLeagueId == "all") classes("active")
                onClick { selectLeague("all") }
            }) { Text("Všechny ligy") }
            leagues.forEach { league ->
                Button(attrs = {
                    if (selectedLeagueId == league.id) classes("active")
                    onClick { selectLeague(league.id) }
                }) { Text(league.name) }
            }
            if (currentRound != null) {
                Button(attrs = {
                    classes("btn-today")
                    onClick { document.getElementById("sched-round-$currentRound")?.scrollIntoView() }
                }) { Text("Dnes") }
            }
        }

        val filtered = if (selectedLeagueId == "all") matches else matches.filter { it.seasonLeagueId == selectedLeagueId }
        val slotByRound = remember(roundSlots) { roundSlots.associateBy { it.roundNumber } }

        // "Všechny ligy": kola napříč ligami se prolínají chronologicky (fáze, číslo kola, pak liga);
        // jedna liga: jen podle čísla kola, název ligy v hlavičce navíc není potřeba.
        // Nadstavba (spec 12): O titul and O udržení share the same round -- one section, not two.
        val groups = filtered
            .groupBy { Triple(it.seasonLeague?.name ?: "", canonicalPlayoffPhase(it.phase), it.roundNumber) }
            .entries
            .sortedWith(
                if (selectedLeagueId == "all") {
                    compareBy({ phaseOrder(it.key.second) }, { it.key.third }, { it.key.first })
                } else {
                    compareBy({ phaseOrder(it.key.second) }, { it.key.third })
                }
            )

        val firstGroupIndexForRound = remember(groups) {
            val seen = mutableSetOf<Int>()
            groups.mapIndexedNotNull { index, entry ->
                val round = entry.key.third
                if (seen.add(round)) round to index else null
            }.toMap()
        }

        groups.forEachIndexed { index, (key, roundMatches) ->
            val (leagueName, phase, round) = key
            val slot = slotByRound[round]
            H3(attrs = {
                if (firstGroupIndexForRound[round] == index) attr("id", "sched-round-$round")
            }) {
                Text(
                    "${if (selectedLeagueId == "all") "$leagueName — " else ""}" +
                        roundSlotLabel(phase, round) +
                        (slot?.let { " (${dateRangeLabel(it.dateFrom, it.dateTo)})" } ?: "")
                )
            }
            Table(attrs = { classes("schedule-table", "public-table") }) {
                Tr {
                    Th { Text("Domácí") }
                    Th { Text("Hosté") }
                    Th { Text("Výsledek") }
                }
                roundMatches.forEach { m ->
                    Tr {
                        Td { Text(teamNames[m.homeTeamId] ?: m.homeTeamId) }
                        Td { Text(teamNames[m.awayTeamId] ?: m.awayTeamId) }
                        Td {
                            if (m.status == "completed") {
                                Button(attrs = { onClick { selectedMatch = m } }) {
                                    Text("${m.homeDuelPoints ?: 0}:${m.awayDuelPoints ?: 0}")
                                }
                            } else {
                                Text("neodehráno")
                            }
                        }
                    }
                }
            }
        }

        selectedMatch?.let { match ->
            MatchDetailModal(
                matchId = match.id,
                homeDuelPoints = match.homeDuelPoints,
                awayDuelPoints = match.awayDuelPoints,
                homeTeamName = teamNames[match.homeTeamId] ?: match.homeTeamId,
                awayTeamName = teamNames[match.awayTeamId] ?: match.awayTeamId,
                leagueName = match.seasonLeague?.name,
                onClose = { selectedMatch = null }
            )
        }
    }
}
